import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { HttpStatus } from 'aem-libs-api/src/constants/httpStatus'
import type {
  ExtraordinaryInstallmentFeeErrorResponse,
  ExtraordinaryInstallmentFeeQuery
} from 'aem-libs-api/src/services/building-savings-product/v1/types'
import { type HttpClientErrorResponse, isHttpClientErrorResponse } from 'aem-libs-api/src/utility/HttpClient'
import { apiRequest } from 'aem-libs-react-ts/src/services/apiRequest'

import { QUERY_CACHE } from '../constants'
import { burinkaApi } from '../services/apiServices'
import { type ExtraordinaryInstallmentInputType, earlyRepaymentStore } from '../store'

const mapRequest = (values: ExtraordinaryInstallmentInputType): ExtraordinaryInstallmentFeeQuery => ({
  accnr: values.accnr,
  ...(values.isWillingToRepayWholeAmount ? {} : { amount: values.amount }),
  date: values.date
})

const isAccountNumberValidationError = (error: unknown): boolean => {
  return (
    isHttpClientErrorResponse(error) &&
    error.response.status === HttpStatus.NOT_FOUND &&
    error.data !== null &&
    (error.data as ExtraordinaryInstallmentFeeErrorResponse).errors[0]?.errorDescription === 'Úvěr nenalezen'
  )
}

const calcFee = apiRequest(burinkaApi.extraordinaryInstallmentFee, {
  skipFirstTracking: false,
  trackResponse: true
})

export const useFeeQuery = () => {
  const values = earlyRepaymentStore.useStoreData()

  const [error, setError] = useState<HttpClientErrorResponse<ExtraordinaryInstallmentFeeErrorResponse> | null>(null)

  const { data, isFetching } = useQuery({
    queryKey: [QUERY_CACHE.CALC_RESPONSE, values.accnr, values.amount, values.date, values.isWillingToRepayWholeAmount],
    enabled: !!values.accnr,
    queryFn: async () => {
      try {
        return await calcFee(mapRequest(values))
      } catch (error) {
        if (isAccountNumberValidationError(error)) {
          setError(error as HttpClientErrorResponse<ExtraordinaryInstallmentFeeErrorResponse>)
          return null
        }
      }
    },
    placeholderData: (response) => response
  })

  return {
    response: data?.data,
    isFetching,
    error
  }
}
